import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';
import toast from 'react-hot-toast';
import './Auth.css';

const GOOGLE_ENABLED = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"/>
      <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}

function GoogleLoginButton({ disabled, onAuthSuccess }) {
  const [gLoading, setGLoading] = useState(false);

  const loginWithGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGLoading(true);
      try {
        await onAuthSuccess(tokenResponse.access_token);
      } finally {
        setGLoading(false);
      }
    },
    onError: (err) => {
      setGLoading(false);
      if (err?.error === 'popup_blocked_by_browser') {
        toast.error('Popup was blocked — please allow popups for this site and try again.');
      } else if (err?.error === 'access_denied') {
        toast.error('Google sign-in was denied. Please try again.');
      } else {
        toast.error('Google sign-in failed. Please try again or use email/password.');
      }
    },
    onNonOAuthError: (err) => {
      if (err?.type !== 'popup_closed') {
        toast.error('Google sign-in failed. Please try again.');
      }
      setGLoading(false);
    },
    flow: 'implicit',
    scope: 'openid email profile',
  });

  return (
    <button
      type="button"
      className="google-btn"
      onClick={() => { setGLoading(true); loginWithGoogle(); }}
      disabled={disabled || gLoading}
    >
      {gLoading ? <span className="google-btn-spinner" /> : <GoogleIcon />}
      <span>{gLoading ? 'Signing in…' : 'Continue with Google'}</span>
    </button>
  );
}

export default function Login() {
  const [form, setForm]             = useState({ email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showPass, setShowPass]     = useState(false);
  const { user, loading, login, googleLogin } = useAuth();
  const navigate = useNavigate();

  // Already logged in → skip the form
  useEffect(() => {
    if (!loading && user) navigate('/dashboard', { replace: true });
  }, [loading, user, navigate]);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(form.email, form.password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      let msg;
      if (err.response?.status === 503) {
        msg = 'Server is starting up — please try again in 10 seconds';
      } else if (err.response?.data?.error) {
        msg = err.response.data.error;
      } else if (err.response?.data?.errors?.[0]?.msg) {
        msg = err.response.data.errors[0].msg;
      } else if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED' || !err.response) {
        msg = 'Cannot reach server — it may be starting up. Please wait 30 seconds and try again.';
      } else {
        msg = err.message || 'Login failed';
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleAuth = async (accessToken) => {
    try {
      const data = await googleLogin(null, accessToken);
      toast.success('Welcome' + (data.user?.name ? `, ${data.user.name.split(' ')[0]}!` : '!'));
      navigate(data.user?.profileCompleted ? '/dashboard' : '/complete-profile');
    } catch (err) {
      const msg = err.response?.data?.error;
      if (err.response?.status === 503) {
        toast.error('Server is starting up — please wait 10 seconds and try again.');
      } else if (msg) {
        toast.error(msg);
      } else {
        toast.error('Google sign-in failed. Please try again.');
      }
    }
  };

  // Don't flash the form while auth state resolves
  if (loading) return null;

  return (
    <div className="auth-page">
      <div className="auth-card fade-in-up">
        <Link to="/" className="auth-logo">▲ AlphaVault</Link>
        <h2>Welcome back</h2>
        <p className="auth-sub">Log in to your trading account</p>

        {GOOGLE_ENABLED && (
          <>
            <GoogleLoginButton disabled={submitting} onAuthSuccess={handleGoogleAuth} />
            <div className="auth-divider"><span>or continue with email</span></div>
          </>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="input-group">
            <label className="input-label">Email address</label>
            <input
              name="email" type="email" required
              className="input-field"
              placeholder="you@example.com"
              value={form.email}
              onChange={handleChange}
              autoComplete="email"
            />
          </div>
          <div className="input-group">
            <label className="input-label">
              Password
              <button
                type="button"
                className="show-pass-btn"
                onClick={() => setShowPass(p => !p)}
                tabIndex={-1}
              >
                {showPass ? 'Hide' : 'Show'}
              </button>
            </label>
            <input
              name="password"
              type={showPass ? 'text' : 'password'}
              required
              className="input-field"
              placeholder="••••••••"
              value={form.password}
              onChange={handleChange}
              autoComplete="current-password"
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary auth-submit"
            disabled={submitting}
          >
            {submitting
              ? <><span className="btn-spinner" /> Logging in…</>
              : 'Log In'
            }
          </button>
        </form>

        <p className="auth-switch">
          Don't have an account? <Link to="/register">Sign up free</Link>
        </p>
      </div>
    </div>
  );
}
